import { reactive, computed } from "vue";
import { useStore } from "vuex";
import type { FormRules } from "element-plus";
import { useFieldService, DEFAULT_FIELD_SERVICE_INFO, type FieldServiceInfo } from "./useFieldService";
import { useDropdownLoaders } from "./useDropdownLoaders";
import type {
  SiteItem,
  JobItem,
  OverheadTaskItem,
  ActivityTaskItem,
  ApiError,
} from "./types";
import { createConditionalValidator } from "./types";

type Task = OverheadTaskItem | ActivityTaskItem;

type ForceTaskInfo = {
  overHead: boolean;
  siteProjectId?: string;
  siteName?: string;
  jobProjectId?: string;
  jobName?: string;
  activityId?: string;
  activityName?: string;
  npCode?: string;
  npCodeName?: string;
  comments: string;
} & FieldServiceInfo;

type ForceTaskState = {
  taskInfo: ForceTaskInfo;
  dropdownSite: SiteItem[];
  dropdownJob: JobItem[];
  dropdownTask: Task[];
  dropdownActivity: ActivityTaskItem[];
  loadingSite: boolean;
  loadingJob: boolean;
  loadingTask: boolean;
  loadingActivity: boolean;
  error: ApiError | null;
};

export default function useForceTaskDetail() {
  const store = useStore();
  const dropdownLoaders = useDropdownLoaders("force");

  const DEFAULT_TASK_INFO: ForceTaskInfo = {
    overHead: false,
    siteProjectId: undefined,
    siteName: undefined,
    jobProjectId: undefined,
    jobName: undefined,
    activityId: undefined,
    activityName: undefined,
    npCode: undefined,
    npCodeName: undefined,
    comments: "",
    ...DEFAULT_FIELD_SERVICE_INFO,
  };

  const state = reactive<ForceTaskState>({
    taskInfo: { ...DEFAULT_TASK_INFO },
    dropdownSite: [],
    dropdownJob: [],
    dropdownTask: [],
    dropdownActivity: [],
    loadingSite: false,
    loadingJob: false,
    loadingTask: false,
    loadingActivity: false,
    error: null,
  });

  const isNonProductive = () => state.taskInfo.overHead;
  const { allowInputFieldService, sigOption, fieldServiceRules, uploadSignatures } =
    useFieldService(state, isNonProductive);

  const rules: FormRules = {
    siteProjectId: [
      { required: true, message: "Please select Site", trigger: ["blur", "change"] },
    ],
    jobProjectId: [
      createConditionalValidator(
        () => !state.taskInfo.overHead,
        "Please select Job/Project",
        ["blur", "change"],
      ),
    ],
    activityId: [
      createConditionalValidator(
        () => !state.taskInfo.overHead,
        "Please select Activity",
        ["blur", "change"],
      ),
    ],
    npCode: [
      createConditionalValidator(
        () => state.taskInfo.overHead,
        "Please select NP Code",
        ["blur", "change"],
      ),
    ],
    comments: [
      { required: true, message: "Please enter Task Description", trigger: ["blur", "change"] },
    ],
    ...fieldServiceRules,
  };

  const setSite = async (siteProjectId: string) => {
    if (!siteProjectId) {
      state.taskInfo.siteProjectId = undefined;
      state.taskInfo.siteName = undefined;
      loadProjectsFromApi();
      loadActivitiesFromApi();
    } else {
      state.taskInfo.siteProjectId = siteProjectId;
      const site = state.dropdownSite.find((s) => s.siteProjectId === siteProjectId);
      state.taskInfo.siteName = site?.siteName || undefined;
      loadProjectsFromApi(siteProjectId);
    }

    state.taskInfo.jobProjectId = undefined;
    state.taskInfo.jobName = undefined;
    state.taskInfo.activityId = undefined;
    state.taskInfo.activityName = undefined;
    state.dropdownJob = [];
    state.dropdownActivity = [];
  };

  const setJob = async (jobProjectId: string, preserveActivity = false) => {
    if (!jobProjectId) {
      state.taskInfo.jobProjectId = undefined;
      state.taskInfo.jobName = undefined;
    } else {
      state.taskInfo.jobProjectId = jobProjectId;
      let job = state.dropdownJob.find((j) => j.jobProjectId === jobProjectId);

      if (!job) {
        try {
          const fetchedJob = await dropdownLoaders.fetchProjectById(jobProjectId);
          if (fetchedJob) {
            job = fetchedJob;
            state.taskInfo.jobName = fetchedJob.jobName;
            state.dropdownJob.push(fetchedJob);
          }
        } catch {}
      } else {
        state.taskInfo.jobName = job.jobName || undefined;
      }

      if (!state.taskInfo.overHead && !preserveActivity) {
        loadActivitiesFromApi(jobProjectId);
      }

      if (job?.siteProjectId && state.taskInfo.siteProjectId !== job.siteProjectId) {
        const site = await injectSiteIfNeeded(job.siteProjectId);
        if (site) {
          state.taskInfo.siteProjectId = site.siteProjectId;
          state.taskInfo.siteName = site.siteName;
        }
      }
    }

    if (!preserveActivity) {
      state.taskInfo.activityId = undefined;
      state.taskInfo.activityName = undefined;
      state.dropdownActivity = [];
    }
  };

  const loadActivitiesFromApi = async (projectId?: string) => {
    const pid = projectId || state.taskInfo.jobProjectId;
    state.loadingActivity = true;
    try {
      const results = await dropdownLoaders.loadActivities(pid);
      const currentId = state.taskInfo.activityId;
      if (currentId && !results.some((a: any) => a.activityId === currentId)) {
        const existingInjected = state.dropdownActivity.find((a) => a.activityId === currentId);
        const currentItem =
          existingInjected && existingInjected.activityName && existingInjected.activityName !== currentId
            ? existingInjected
            : {
                activityId: currentId,
                activityName: state.taskInfo.activityName || currentId,
              };
        state.dropdownActivity = [currentItem, ...results];
      } else {
        state.dropdownActivity = results;
      }
    } finally {
      state.loadingActivity = false;
    }
  };

  const searchActivities = async (query: string) => {
    state.loadingActivity = true;
    try {
      const results = await dropdownLoaders.searchActivities(query, state.taskInfo.jobProjectId);
      const currentId = state.taskInfo.activityId;
      if (currentId && !results.some((a: any) => a.activityId === currentId)) {
        const existingInjected = state.dropdownActivity.find((a) => a.activityId === currentId);
        const currentItem =
          existingInjected && existingInjected.activityName && existingInjected.activityName !== currentId
            ? existingInjected
            : {
                activityId: currentId,
                activityName: state.taskInfo.activityName || currentId,
              };
        state.dropdownActivity = [currentItem, ...results];
      } else {
        state.dropdownActivity = results;
      }
    } finally {
      state.loadingActivity = false;
    }
  };

  const setActivity = (activityId: string) => {
    if (!activityId) {
      state.taskInfo.activityId = undefined;
      state.taskInfo.activityName = undefined;
      return;
    }

    state.taskInfo.activityId = activityId;
    const activity = state.dropdownActivity.find((a: any) => a.activityId === activityId);
    state.taskInfo.activityName = activity?.activityName || undefined;

    if (activity?.jobProjectId && state.taskInfo.jobProjectId !== activity.jobProjectId) {
      state.taskInfo.jobProjectId = activity.jobProjectId;
      setJob(activity.jobProjectId, true);
    } else if (activity?.jobProjectId && (!state.taskInfo.siteProjectId || !state.taskInfo.jobProjectId)) {
      setJob(activity.jobProjectId, true);
    }
  };

  const loadTasksForOverhead = async () => {
    state.loadingTask = true;
    try {
      state.dropdownTask = await dropdownLoaders.loadOverheadTaskCodes();
    } finally {
      state.loadingTask = false;
    }
  };

  const setTask = (taskId: string) => {
    if (!taskId) {
      state.taskInfo.npCode = undefined;
      state.taskInfo.npCodeName = undefined;
      state.taskInfo.activityId = undefined;
      state.taskInfo.activityName = undefined;
      return;
    }

    if (state.taskInfo.overHead) {
      state.taskInfo.npCode = taskId;
      const task = state.dropdownTask.find((t: any) => t.npCode === taskId);
      state.taskInfo.npCodeName = (task as any)?.npCodeName || undefined;
    } else {
      state.taskInfo.activityId = taskId;
      const task = state.dropdownTask.find((t: any) => t.activityId === taskId);
      state.taskInfo.activityName = (task as any)?.activityName || undefined;
    }
  };

  const setTaskDescription = (description: string) => {
    state.taskInfo.comments = description;
  };

  const setFieldService = (enabled: boolean) => {
    state.taskInfo.additionInfo = enabled;
  };

  const setOverhead = (value: boolean) => {
    state.taskInfo.overHead = value;
    if (value) {
      state.taskInfo.jobProjectId = undefined;
      state.taskInfo.jobName = undefined;
      state.taskInfo.activityId = undefined;
      state.taskInfo.activityName = undefined;
      loadTasksForOverhead();
    }
  };

  const loadSitesFromApi = async () => {
    state.loadingSite = true;
    try {
      const results = await dropdownLoaders.loadSites();
      const currentId = state.taskInfo.siteProjectId;
      if (currentId && !results.some((s: any) => s.siteProjectId === currentId)) {
        const existingInjected = state.dropdownSite.find((s) => s.siteProjectId === currentId);
        const currentItem =
          existingInjected && existingInjected.siteName && existingInjected.siteName !== currentId
            ? existingInjected
            : {
                siteProjectId: currentId,
                siteName: state.taskInfo.siteName || currentId,
              };
        state.dropdownSite = [currentItem, ...results];
      } else {
        state.dropdownSite = results;
      }
    } finally {
      state.loadingSite = false;
    }
  };

  const searchSites = async (query: string) => {
    state.loadingSite = true;
    try {
      const results = await dropdownLoaders.searchSites(query);
      const currentId = state.taskInfo.siteProjectId;
      if (currentId && !results.some((s: any) => s.siteProjectId === currentId)) {
        const existingInjected = state.dropdownSite.find((s) => s.siteProjectId === currentId);
        const currentItem =
          existingInjected && existingInjected.siteName && existingInjected.siteName !== currentId
            ? existingInjected
            : {
                siteProjectId: currentId,
                siteName: state.taskInfo.siteName || currentId,
              };
        state.dropdownSite = [currentItem, ...results];
      } else {
        state.dropdownSite = results;
      }
    } finally {
      state.loadingSite = false;
    }
  };

  const loadProjectsFromApi = async (siteProjectId?: string) => {
    const sid = siteProjectId || state.taskInfo.siteProjectId;
    state.loadingJob = true;
    try {
      const results = await dropdownLoaders.loadProjects(sid);
      const currentId = state.taskInfo.jobProjectId;
      if (currentId && !results.some((j: any) => j.jobProjectId === currentId)) {
        const existingInjected = state.dropdownJob.find((j) => j.jobProjectId === currentId);
        const currentItem =
          existingInjected && existingInjected.jobName && existingInjected.jobName !== currentId
            ? existingInjected
            : {
                jobProjectId: currentId,
                jobName: state.taskInfo.jobName || currentId,
              };
        state.dropdownJob = [currentItem, ...results];
      } else {
        state.dropdownJob = results;
      }
    } finally {
      state.loadingJob = false;
    }
  };

  const searchProjects = async (query: string) => {
    state.loadingJob = true;
    try {
      const results = await dropdownLoaders.searchProjects(query, state.taskInfo.siteProjectId);
      const currentId = state.taskInfo.jobProjectId;
      if (currentId && !results.some((j: any) => j.jobProjectId === currentId)) {
        const existingInjected = state.dropdownJob.find((j) => j.jobProjectId === currentId);
        const currentItem =
          existingInjected && existingInjected.jobName && existingInjected.jobName !== currentId
            ? existingInjected
            : {
                jobProjectId: currentId,
                jobName: state.taskInfo.jobName || currentId,
              };
        state.dropdownJob = [currentItem, ...results];
      } else {
        state.dropdownJob = results;
      }
    } finally {
      state.loadingJob = false;
    }
  };

  const loadOverheadTasksFromApi = async () => {
    if (state.taskInfo.overHead) {
      await loadTasksForOverhead();
    }
  };

  const searchTasks = async (query: string) => {
    state.loadingTask = true;
    try {
      state.dropdownTask = await dropdownLoaders.searchOverheadTaskCodes(query);
    } finally {
      state.loadingTask = false;
    }
  };

  const injectJobIfNeeded = async (siteId: string, jobId: string) => {
    if (!siteId || !jobId) return null;
    const existing = state.dropdownJob.find((j) => j.jobProjectId === jobId);
    if (existing && existing.jobName && existing.jobName !== jobId) return existing;

    try {
      const result = await store.dispatch("v2/fetchJobById", { siteId, jobId });
      if (result) {
        const jobItem = {
          jobProjectId: result.id || result.projectId,
          jobName: result.name,
          description: result.description,
          siteProjectId: result.siteId,
        };
        state.dropdownJob = [jobItem, ...state.dropdownJob.filter((j) => j.jobProjectId !== jobId)];
        return jobItem;
      }
    } catch {}
    return null;
  };

  const injectActivityIfNeeded = async (projectId: string, activityId: string) => {
    if (!projectId || !activityId) return null;
    const existing = state.dropdownActivity.find((a) => a.activityId === activityId);
    if (existing && existing.activityName && existing.activityName !== activityId) return existing;

    try {
      const result = await store.dispatch("v2/fetchActivityById", { projectId, activityId });
      if (result) {
        const activityItem = {
          activityId: result.id,
          activityName: result.name,
          description: result.description,
          projectId: result.projectId,
        };
        state.dropdownActivity = [
          activityItem,
          ...state.dropdownActivity.filter((a) => a.activityId !== activityId),
        ];
        return activityItem;
      }
    } catch {}
    return null;
  };

  const injectSiteIfNeeded = async (siteId: string) => {
    if (!siteId) return null;
    const existing = state.dropdownSite.find((s) => s.siteProjectId === siteId);
    if (existing && existing.siteName && existing.siteName !== siteId) return existing;

    try {
      const result = await store.dispatch("v2/fetchSiteById", siteId);
      if (result) {
        const siteItem = {
          siteProjectId: result.id || result.projectId,
          siteName: result.name,
          companyId: result.companyId,
        };
        state.dropdownSite = [siteItem, ...state.dropdownSite.filter((s) => s.siteProjectId !== siteId)];
        return siteItem;
      }
    } catch {}
    return null;
  };

  return {
    state,
    rules,
    allowInputFieldService,
    sigOption,
    loadSitesFromApi,
    searchSites,
    loadProjectsFromApi,
    searchProjects,
    loadOverheadTasksFromApi,
    searchTasks,
    loadActivitiesFromApi,
    searchActivities,
    setActivity,
    setSite,
    setJob,
    setTask,
    setTaskDescription,
    setFieldService,
    setOverhead,
    uploadSignatures,
    injectJobIfNeeded,
    injectActivityIfNeeded,
    injectSiteIfNeeded,
  };
}
